import { apiLogin } from '@api/apiCall';
import BtnSMS from '@atoms/BtnSMS';
import BtnWhatsapp from '@atoms/BtnWhatsapp';
import ChatIcon from '@atoms/ChatIcon';
import LoginPhoneTxt from '@atoms/LoginPhoneTxt';
import LoginWelcomeTxt from '@atoms/LoginWelcomeTxt';
import TextWithValue from '@atoms/TextWithValue';
import { shouldHideSplashScreen } from '@atoms/ValidateToken/validateTokenUtils';
import { Box } from '@components/primitives';
import useKeyboardStatus from '@customHooks/useKeyboardStatus';
import LoginInfoSheet from '@molecules/LoginInfoSheet';
import LoginPhoneInput from '@molecules/LoginPhoneInput';
import WelcomCards from '@molecules/Welcome';
import { useNavigation } from '@react-navigation/native';
import { getStore } from '@store/storeUtils';
import { useMutation } from '@tanstack/react-query';
import colors from '@theme/colors';
import AmplitudeHelper from '@utils/analytics';
import { AMPLITUDE_CONSTANTS, AMPLITUDE_EVENT_SOURCES } from '@utils/analytics/constants';
import { logoutFromMoengage } from '@utils/engagement/engagementUtils';
import secureStore from '@utils/secureStore';
import { formatPhoneNumber, formatUnderScoreLowerCase, goBackToHome, returnBottomSheetContent } from '@utils/utils';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyboardAvoidingView, Platform, StatusBar, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import {
  onCloseFunction,
  onPressPrimaryFunction,
  onPressSecondaryFunction,
  phoneChangeHandler,
  shouldNavigateOnSuccess,
  showLoginErrors,
} from './loginUtils';
import styles from './styles';
import { LOGIN_INFO_SHEET_CONTENT, LOGIN_INFO_SHEET_KEYS } from '../../constants';

interface LoginButtonsProps {
  isDisabled: boolean;
  isLoadingSMS: boolean;
  phoneNumber: string;
  isLoadingWhatsapp: boolean;
  performLogin: (payload: any) => void;
  setIsLoadingSMS: (v: boolean) => void;
  setIsLoadingWhatsapp: (v: boolean) => void;
}

function LoginButtons({
  isDisabled,
  isLoadingSMS,
  phoneNumber,
  isLoadingWhatsapp,
  performLogin,
  setIsLoadingSMS,
  setIsLoadingWhatsapp,
}: LoginButtonsProps) {
  return (
    <Box marginTop="32px" flexDirection="row">
      <BtnWhatsapp
        isDisabled={isDisabled}
        isLoadingWhatsapp={isLoadingWhatsapp}
        phoneNumber={phoneNumber}
        performLogin={performLogin}
        setIsLoadingSMS={setIsLoadingSMS}
        setIsLoadingWhatsapp={setIsLoadingWhatsapp}
      />
      <BtnSMS
        isDisabled={isDisabled}
        isLoadingSMS={isLoadingSMS}
        phoneNumber={phoneNumber}
        performLogin={performLogin}
        setIsLoadingSMS={setIsLoadingSMS}
        setIsLoadingWhatsapp={setIsLoadingWhatsapp}
      />
    </Box>
  );
}

function GGLogin(): React.JSX.Element {
  const navigation = useNavigation();
  const { t } = useTranslation('common');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isDisabled, setIsDisabled] = useState(true);
  const [isLoadingSMS, setIsLoadingSMS] = useState(false);
  const [isLoadingWhatsapp, setIsLoadingWhatsapp] = useState(false);
  const [sError, setError] = useState('');
  const [infoModal, setInfoModal] = useState({
    visible: false,
    data: returnBottomSheetContent(
      LOGIN_INFO_SHEET_KEYS.RESEND_OTP,
      LOGIN_INFO_SHEET_CONTENT[LOGIN_INFO_SHEET_KEYS.RESEND_OTP],
      phoneNumber,
    ),
    errorCode: LOGIN_INFO_SHEET_KEYS.RESEND_OTP,
  });
  const { tenantSettings, setLoginData } = getStore();
  const { countryCode } = tenantSettings;
  const {
    mutate: performLogin,
    isSuccess,
    data: LoginData,
    isError,
    error,
  } = useMutation({ mutationFn: (payload: any) => apiLogin(payload) });

  const isKeyboardOpen = useKeyboardStatus();

  useEffect(() => {
    AmplitudeHelper.clearUserProperties();
    logoutFromMoengage();
    shouldHideSplashScreen();
    (async () => {
      if (Platform.OS === 'web') {
        const token = await secureStore.getItemAsync('accessToken');
        if (token && token.length > 0) {
          goBackToHome(navigation, true);
        }
      }
    })();
  }, []);

  useEffect(() => {
    showLoginErrors(isError, error, setIsLoadingSMS, setIsLoadingWhatsapp, phoneNumber, setInfoModal);
  }, [isError, error]);

  useEffect(() => {
    shouldNavigateOnSuccess(
      LoginData,
      isSuccess,
      setLoginData,
      setIsLoadingSMS,
      setIsLoadingWhatsapp,
      navigation,
      phoneNumber,
    );
  }, [LoginData, isSuccess]);

  const onChangePhoneNumber = phoneChangeHandler(sError, setError, setPhoneNumber, setIsDisabled, t, countryCode);

  const onPressPrimaryModalButton = () => {
    onPressPrimaryFunction(infoModal, phoneNumber, setInfoModal);
  };

  const onPressSecondaryModalButton = () => {
    onPressSecondaryFunction(infoModal, phoneNumber, setInfoModal, setPhoneNumber, navigation);
  };

  const onCloseModal = () => {
    onCloseFunction(infoModal, phoneNumber, setInfoModal);
  };

  useEffect(() => {
    AmplitudeHelper.logEvent(AMPLITUDE_CONSTANTS.authentication.login_page.name);
  }, []);

  const loginHandler = (payload: any) => {
    const e = AMPLITUDE_CONSTANTS.authentication.otp_requested;
    AmplitudeHelper.logEvent(e.name, {
      [e.attributes.phone_number]: formatPhoneNumber(payload.mobileNumber),
      [e.attributes.source]: formatUnderScoreLowerCase(payload.otpType),
    });
    performLogin(payload);
  };

  const onTermsHandler = () => {
    const e = AMPLITUDE_CONSTANTS.authentication.terms_clicked;
    AmplitudeHelper.logEvent(e.name, {
      [e.attributes.phone_number]: formatPhoneNumber(phoneNumber),
      [e.attributes.source]: AMPLITUDE_EVENT_SOURCES.login_page_source.login_page,
    });
    navigation.navigate('TermsAndCondition');
  };

  return (
    <>
      <StatusBar backgroundColor={colors.secondary.orchid} translucent hidden={false} />
      <SafeAreaView style={styles.loginParent} edges={['top']}>
        <View style={styles.keyboardCoverContainer}>
          <KeyboardAvoidingView style={styles.keyboardStyle} behavior="height" keyboardVerticalOffset={30}>
            <WelcomCards
              isKeyboardOpen={isKeyboardOpen}
              item={{ cardColor: colors.secondary.orchid, textContext: t('login.loginHelpText') }}
            />
            <View style={styles.loginInputContainer}>
              <View style={styles.loginInputParent}>
                <LoginWelcomeTxt />
                <LoginPhoneTxt />
                <LoginPhoneInput sError={sError} phoneNumber={phoneNumber} setPhoneNumber={onChangePhoneNumber} />
                <LoginButtons
                  isLoadingSMS={isLoadingSMS}
                  isLoadingWhatsapp={isLoadingWhatsapp}
                  isDisabled={isDisabled}
                  phoneNumber={phoneNumber}
                  performLogin={loginHandler}
                  setIsLoadingSMS={setIsLoadingSMS}
                  setIsLoadingWhatsapp={setIsLoadingWhatsapp}
                />
                <LoginInfoSheet
                  loginInfo={infoModal}
                  onPressPrimary={onPressPrimaryModalButton}
                  onPressSecondary={onPressSecondaryModalButton}
                  onClose={onCloseModal}
                />
                <Box testID="txtTermsAndConditions" style={styles.tncParent}>
                  <TextWithValue
                    textToShow="login.tncText"
                    valueText="login.tncHyperLink"
                    onValuePress={onTermsHandler}
                    isValueHyperLink
                    color={colors.neutral.darkMist}
                    textSize="xsm-bold"
                    valueSize="xsm-bold"
                    textWeight="700"
                    valueWeight="700"
                    textAlign="center"
                  />
                </Box>
              </View>
            </View>
          </KeyboardAvoidingView>
          <ChatIcon />
        </View>
      </SafeAreaView>
    </>
  );
}

export default GGLogin;
